/**
 * Specialisation Carousel (section 3005, CMS variant 3000).
 *
 * One widget, two layouts — `design_variant` is the dropdown that switches
 * between image cards (variant 1, default) and icon cards (variant 2).
 * Cards live in the metadata table; each row carries its own click behaviour,
 * an optional link to a global course (which supplies the brochure) and a
 * rich-text modal body.
 */
export const SPECIALISATION_CAROUSEL_1_CONSTANTS = {
  METADATA_TITLES: {
    metadata: "Cards",
  },

  DEFAULT_ROW: {
    id: "1",
    meta_title: "",
    meta_image: "",
    meta_image_alt: "",
    meta_icon: "",
    meta_icon_alt: "",
    meta_arrow_icon: "",
    meta_arrow_icon_alt: "",
    meta_click_action: "lead_form",
    meta_url: "",
    meta_open_in_new_tab: false,
    meta_course_id: "",
    meta_brochure_url: "",
    meta_modal_title: "",
    meta_modal_body: "",
    meta_modal_primary_cta_text: "",
    meta_modal_brochure_cta_text: "",
    meta_show_modal_brochure_cta: true,
    meta_priority: 1,
    meta_status: 1,
  },

  METADATA_FIELDS: [
    {
      header: "Card title",
      fieldName: "meta_title",
      value: "",
      valueType: "string",
      filedType: "INPUT",
      placeholder: "Product Management",
      required: true,
      defaultValue: "",
    },
    {
      header: "Image (layout 1)",
      fieldName: "meta_image",
      value: "",
      valueType: "string",
      filedType: "FILE",
      placeholder: "Upload card image",
      defaultValue: "",
    },
    {
      header: "Image alt text",
      fieldName: "meta_image_alt",
      value: "",
      valueType: "string",
      filedType: "INPUT",
      placeholder: "Describe the image",
      defaultValue: "",
    },
    {
      header: "Icon (layout 2)",
      fieldName: "meta_icon",
      value: "",
      valueType: "string",
      filedType: "FILE",
      placeholder: "Upload card icon",
      defaultValue: "",
    },
    {
      header: "Icon alt text",
      fieldName: "meta_icon_alt",
      value: "",
      valueType: "string",
      filedType: "INPUT",
      placeholder: "Describe the icon",
      defaultValue: "",
    },
    {
      header: "Arrow icon (overrides default)",
      fieldName: "meta_arrow_icon",
      value: "",
      valueType: "string",
      filedType: "FILE",
      placeholder: "Upload arrow icon",
      defaultValue: "",
    },
    {
      header: "Arrow icon alt text",
      fieldName: "meta_arrow_icon_alt",
      value: "",
      valueType: "string",
      filedType: "INPUT",
      placeholder: "Describe the arrow icon",
      defaultValue: "",
    },
    {
      header: "On click",
      fieldName: "meta_click_action",
      value: "lead_form",
      valueType: "string",
      filedType: "SELECT",
      placeholder: "Select behaviour",
      defaultValue: "lead_form",
      options: [
        { value: "lead_form", label: "Open lead form (default)" },
        { value: "url", label: "Redirect to URL" },
        { value: "modal", label: "Open details modal" },
      ],
    },
    {
      header: "Redirect URL (used when On click = Redirect)",
      fieldName: "meta_url",
      value: "",
      valueType: "string",
      filedType: "INPUT",
      placeholder: "https://… or /program/mba",
      defaultValue: "",
    },
    {
      header: "Open URL in new tab",
      fieldName: "meta_open_in_new_tab",
      value: false,
      valueType: "boolean",
      filedType: "TOGGLE",
      placeholder: "",
      defaultValue: false,
    },
    {
      header: "Course",
      fieldName: "meta_course_id",
      value: "",
      valueType: "string",
      filedType: "SELECT",
      placeholder: "Select course",
      defaultValue: "",
      options: [],
      // Copies the selected course's brochure onto the row so the download
      // lead form has a link even if the course list is unavailable later.
      linkedField: "meta_brochure_url",
      linkedValueKey: "brochure",
      dynamicOptions: "courses",
    },
    {
      header: "Modal title",
      fieldName: "meta_modal_title",
      value: "",
      valueType: "string",
      filedType: "INPUT",
      placeholder: "Falls back to the card title",
      defaultValue: "",
    },
    {
      header: "Modal body (rich text)",
      fieldName: "meta_modal_body",
      value: "",
      valueType: "string",
      filedType: "RICH_TEXT_EDITOR",
      placeholder: "Programme overview, eligibility, …",
      defaultValue: "",
    },
    {
      header: "Modal apply CTA text",
      fieldName: "meta_modal_primary_cta_text",
      value: "",
      valueType: "string",
      filedType: "INPUT",
      placeholder: "Falls back to the section default",
      defaultValue: "",
    },
    {
      header: "Modal brochure CTA text",
      fieldName: "meta_modal_brochure_cta_text",
      value: "",
      valueType: "string",
      filedType: "INPUT",
      placeholder: "Falls back to the section default",
      defaultValue: "",
    },
    {
      header: "Show brochure CTA in modal",
      fieldName: "meta_show_modal_brochure_cta",
      value: true,
      valueType: "boolean",
      filedType: "TOGGLE",
      placeholder: "",
      defaultValue: true,
    },
    {
      header: "Order",
      fieldName: "meta_priority",
      value: 1,
      valueType: "number",
      filedType: "INPUT",
      placeholder: "1",
      defaultValue: 1,
    },
    {
      header: "Active",
      fieldName: "meta_status",
      value: 1,
      valueType: "number",
      filedType: "TOGGLE",
      placeholder: "",
      defaultValue: 1,
    },
  ],

  SPECIALISATION_CAROUSEL_FIELDS_CONFIG: [
    // ── Layout ───────────────────────────────────────────────────────
    {
      fieldName: "design_variant",
      label: "Card layout",
      value: "variant_1",
      valueType: "string",
      filedType: "SELECT",
      placeholder: "Select card layout",
      required: false,
      defaultValue: "variant_1",
      description:
        "Layout 1 shows a cover image on each card. Layout 2 shows an icon instead.",
      options: [
        { value: "variant_1", label: "Layout 1 — image cards (default)" },
        { value: "variant_2", label: "Layout 2 — icon cards" },
      ],
    },

    // ── Section header ───────────────────────────────────────────────
    {
      fieldName: "title",
      label: "Section title",
      value: "Choose Your Online MBA Programme Specialisation",
      valueType: "string",
      filedType: "INPUT",
      placeholder: "Choose Your Online MBA Programme Specialisation",
      required: false,
      defaultValue: "Choose Your Online MBA Programme Specialisation",
    },
    {
      fieldName: "title_color",
      label: "Section title color",
      value: "#101112",
      valueType: "string",
      filedType: "COLOR",
      placeholder: "#101112",
      required: false,
      defaultValue: "#101112",
    },
    {
      fieldName: "subtitle",
      label: "Section subtitle",
      value: "Industry-aligned curriculum, at par with on-campus programs.",
      valueType: "string",
      filedType: "INPUT",
      placeholder: "Industry-aligned curriculum, at par with on-campus programs.",
      required: false,
      defaultValue:
        "Industry-aligned curriculum, at par with on-campus programs.",
    },
    {
      fieldName: "subtitle_color",
      label: "Section subtitle color",
      value: "#101112",
      valueType: "string",
      filedType: "COLOR",
      placeholder: "#101112",
      required: false,
      defaultValue: "#101112",
    },
    {
      fieldName: "background_color",
      label: "Section background",
      value: "#FFFFFF",
      valueType: "string",
      filedType: "COLOR",
      placeholder: "#FFFFFF",
      required: false,
      defaultValue: "#FFFFFF",
    },

    // ── Card styling ─────────────────────────────────────────────────
    {
      fieldName: "card_background_color",
      label: "Card background",
      value: "#FFFFFF",
      valueType: "string",
      filedType: "COLOR",
      placeholder: "#FFFFFF",
      required: false,
      defaultValue: "#FFFFFF",
    },
    {
      fieldName: "card_title_color",
      label: "Card title color",
      value: "#000000",
      valueType: "string",
      filedType: "COLOR",
      placeholder: "#000000",
      required: false,
      defaultValue: "#000000",
    },
    {
      fieldName: "card_arrow_color",
      label: "Card arrow color",
      value: "#292D32",
      valueType: "string",
      filedType: "COLOR",
      placeholder: "#292D32",
      required: false,
      defaultValue: "#292D32",
      description: "Ignored on cards that upload their own arrow icon.",
    },
    {
      fieldName: "card_icon_color",
      label: "Card icon color (layout 2)",
      value: "#292D32",
      valueType: "string",
      filedType: "COLOR",
      placeholder: "#292D32",
      required: false,
      defaultValue: "#292D32",
      description: "Applies to the built-in icon only.",
    },

    // ── Navigation ───────────────────────────────────────────────────
    {
      fieldName: "show_navigation",
      label: "Show previous / next buttons",
      value: true,
      valueType: "boolean",
      filedType: "TOGGLE",
      placeholder: "",
      required: false,
      defaultValue: true,
    },
    {
      fieldName: "nav_arrow_color",
      label: "Previous / next arrow color",
      value: "#9F1C34",
      valueType: "string",
      filedType: "COLOR",
      placeholder: "#9F1C34",
      required: false,
      defaultValue: "#9F1C34",
    },
    {
      fieldName: "nav_prev_icon",
      label: "Previous button image",
      value: "",
      valueType: "string",
      filedType: "FILE",
      placeholder: "Upload previous icon",
      required: false,
      defaultValue: "",
      description: "Replaces the default previous button entirely.",
    },
    {
      fieldName: "nav_prev_icon_alt",
      label: "Previous button alt text",
      value: "",
      valueType: "string",
      filedType: "INPUT",
      placeholder: "Previous",
      required: false,
      defaultValue: "",
    },
    {
      fieldName: "nav_next_icon",
      label: "Next button image",
      value: "",
      valueType: "string",
      filedType: "FILE",
      placeholder: "Upload next icon",
      required: false,
      defaultValue: "",
      description: "Replaces the default next button entirely.",
    },
    {
      fieldName: "nav_next_icon_alt",
      label: "Next button alt text",
      value: "",
      valueType: "string",
      filedType: "INPUT",
      placeholder: "Next",
      required: false,
      defaultValue: "",
    },

    // ── Scroll indicator ─────────────────────────────────────────────
    {
      fieldName: "show_pagination",
      label: "Show scroll indicator",
      value: true,
      valueType: "boolean",
      filedType: "TOGGLE",
      placeholder: "",
      required: false,
      defaultValue: true,
    },
    {
      fieldName: "scroll_active_color",
      label: "Scroll indicator — active color",
      value: "#9F1C34",
      valueType: "string",
      filedType: "COLOR",
      placeholder: "#9F1C34",
      required: false,
      defaultValue: "#9F1C34",
    },
    {
      fieldName: "scroll_inactive_color",
      label: "Scroll indicator — inactive color",
      value: "#D9D9D9",
      valueType: "string",
      filedType: "COLOR",
      placeholder: "#D9D9D9",
      required: false,
      defaultValue: "#D9D9D9",
    },
    {
      fieldName: "show_scrollbar",
      label: "Show horizontal scrollbar on the cards",
      value: false,
      valueType: "boolean",
      filedType: "TOGGLE",
      placeholder: "",
      required: false,
      defaultValue: false,
    },
    {
      fieldName: "scrollbar_thumb_color",
      label: "Scrollbar color",
      value: "#9F1C34",
      valueType: "string",
      filedType: "COLOR",
      placeholder: "#9F1C34",
      required: false,
      defaultValue: "#9F1C34",
    },
    {
      fieldName: "scrollbar_track_color",
      label: "Scrollbar track color",
      value: "#F1F1F1",
      valueType: "string",
      filedType: "COLOR",
      placeholder: "#F1F1F1",
      required: false,
      defaultValue: "#F1F1F1",
    },

    // ── Section CTA ──────────────────────────────────────────────────
    {
      fieldName: "show_cta",
      label: "Show section CTA",
      value: true,
      valueType: "boolean",
      filedType: "TOGGLE",
      placeholder: "",
      required: false,
      defaultValue: true,
    },
    {
      fieldName: "cta_text",
      label: "CTA text",
      value: "Apply Now",
      valueType: "string",
      filedType: "INPUT",
      placeholder: "Apply Now",
      required: false,
      defaultValue: "Apply Now",
    },
    {
      fieldName: "cta_text_color",
      label: "CTA text color",
      value: "#FFFFFF",
      valueType: "string",
      filedType: "COLOR",
      placeholder: "#FFFFFF",
      required: false,
      defaultValue: "#FFFFFF",
    },
    {
      fieldName: "cta_background_color",
      label: "CTA background",
      value: "#9F1C34",
      valueType: "string",
      filedType: "COLOR",
      placeholder: "#9F1C34",
      required: false,
      defaultValue: "#9F1C34",
    },
    {
      fieldName: "cta_shadow_color",
      label: "CTA shadow color",
      value: "rgba(202,125,139,0.6)",
      valueType: "string",
      filedType: "RGBA",
      placeholder: "rgba(202,125,139,0.6)",
      required: false,
      defaultValue: "rgba(202,125,139,0.6)",
    },
    {
      fieldName: "cta_icon",
      label: "CTA icon",
      value: "",
      valueType: "string",
      filedType: "FILE",
      placeholder: "Upload CTA icon",
      required: false,
      defaultValue: "",
      description: "Replaces the built-in arrow glyph.",
    },
    {
      fieldName: "cta_icon_alt",
      label: "CTA icon alt text",
      value: "",
      valueType: "string",
      filedType: "INPUT",
      placeholder: "Describe the CTA icon",
      required: false,
      defaultValue: "",
    },
    {
      fieldName: "cta_url",
      label: "CTA URL",
      value: "",
      valueType: "string",
      filedType: "INPUT",
      placeholder: "Leave empty to open the lead form",
      required: false,
      defaultValue: "",
    },

    // ── Modal ────────────────────────────────────────────────────────
    {
      fieldName: "modal_header_background_color",
      label: "Modal header background",
      value: "#F5E4E7",
      valueType: "string",
      filedType: "COLOR",
      placeholder: "#F5E4E7",
      required: false,
      defaultValue: "#F5E4E7",
    },
    // Only backgrounds are configured here. Title and body colours are the rich-text
    // editor's job — the editor writes them inline, so a second colour control in the
    // CMS could only fight what the author had already typed.
    {
      fieldName: "modal_body_background_color",
      label: "Modal body background",
      value: "#FFFFFF",
      valueType: "string",
      filedType: "COLOR",
      placeholder: "#FFFFFF",
      required: false,
      defaultValue: "#FFFFFF",
    },
    {
      fieldName: "modal_close_icon_color",
      label: "Modal close icon color",
      value: "#9F1C34",
      valueType: "string",
      filedType: "COLOR",
      placeholder: "#9F1C34",
      required: false,
      defaultValue: "#9F1C34",
    },
    {
      fieldName: "modal_close_icon",
      label: "Modal close icon",
      value: "",
      valueType: "string",
      filedType: "FILE",
      placeholder: "Upload close icon",
      required: false,
      defaultValue: "",
    },
    {
      fieldName: "modal_close_icon_alt",
      label: "Modal close icon alt text",
      value: "",
      valueType: "string",
      filedType: "INPUT",
      placeholder: "Close",
      required: false,
      defaultValue: "",
    },
    {
      fieldName: "modal_primary_cta_text",
      label: "Modal apply CTA text",
      value: "Apply Now",
      valueType: "string",
      filedType: "INPUT",
      placeholder: "Apply Now",
      required: false,
      defaultValue: "Apply Now",
    },
    {
      fieldName: "modal_primary_cta_text_color",
      label: "Modal apply CTA text color",
      value: "#FFFFFF",
      valueType: "string",
      filedType: "COLOR",
      placeholder: "#FFFFFF",
      required: false,
      defaultValue: "#FFFFFF",
    },
    {
      fieldName: "modal_primary_cta_background_color",
      label: "Modal apply CTA background",
      value: "#9F1C34",
      valueType: "string",
      filedType: "COLOR",
      placeholder: "#9F1C34",
      required: false,
      defaultValue: "#9F1C34",
    },
    {
      fieldName: "modal_primary_cta_icon",
      label: "Modal apply CTA icon",
      value: "",
      valueType: "string",
      filedType: "FILE",
      placeholder: "Upload icon",
      required: false,
      defaultValue: "",
    },
    {
      fieldName: "modal_primary_cta_icon_alt",
      label: "Modal apply CTA icon alt text",
      value: "",
      valueType: "string",
      filedType: "INPUT",
      placeholder: "Describe the icon",
      required: false,
      defaultValue: "",
    },
    {
      fieldName: "modal_brochure_cta_text",
      label: "Modal brochure CTA text",
      value: "Download Brochure",
      valueType: "string",
      filedType: "INPUT",
      placeholder: "Download Brochure",
      required: false,
      defaultValue: "Download Brochure",
      description:
        "Shown only when the card is linked to a course that has a brochure.",
    },
    {
      fieldName: "modal_brochure_cta_text_color",
      label: "Modal brochure CTA text color",
      value: "#000000",
      valueType: "string",
      filedType: "COLOR",
      placeholder: "#000000",
      required: false,
      defaultValue: "#000000",
    },
    {
      fieldName: "modal_brochure_cta_background_color",
      label: "Modal brochure CTA background",
      value: "#FFFFFF",
      valueType: "string",
      filedType: "COLOR",
      placeholder: "#FFFFFF",
      required: false,
      defaultValue: "#FFFFFF",
    },
    {
      fieldName: "modal_brochure_cta_border_color",
      label: "Modal brochure CTA border",
      value: "#000000",
      valueType: "string",
      filedType: "COLOR",
      placeholder: "#000000",
      required: false,
      defaultValue: "#000000",
    },
    {
      fieldName: "modal_brochure_cta_icon",
      label: "Modal brochure CTA icon",
      value: "",
      valueType: "string",
      filedType: "FILE",
      placeholder: "Upload icon",
      required: false,
      defaultValue: "",
    },
    {
      fieldName: "modal_brochure_cta_icon_alt",
      label: "Modal brochure CTA icon alt text",
      value: "",
      valueType: "string",
      filedType: "INPUT",
      placeholder: "Describe the icon",
      required: false,
      defaultValue: "",
    },

    {
      fieldName: "status",
      label: "Section Status",
      value: true,
      valueType: "boolean",
      filedType: "TOGGLE",
      placeholder: "Enable/Disable Section",
      defaultValue: true,
    },
  ],
};
